import { v4 as uuid } from 'uuid';
import type { FrameRangeT } from '../../../common/types/types';
import type { OutputRelationshipT } from '../../../modernization-common/relationships';

/**
 * Returns the frame ranges that survive after removing a deleted range.
 * Handles all overlap cases: fully inside, partially before, partially after, spanning.
 */
export const computeRemainingFrameRanges = (frameRanges: FrameRangeT[], deletedRange: FrameRangeT): FrameRangeT[] => {
  const result: FrameRangeT[] = [];
  for (const range of frameRanges) {
    const noOverlap = range.endFrameIndex < deletedRange.startFrameIndex || range.startFrameIndex > deletedRange.endFrameIndex;
    if (noOverlap) {
      result.push(range);
      continue;
    }
    // Surviving segment before the deleted range
    if (range.startFrameIndex < deletedRange.startFrameIndex) {
      result.push({
        startFrameIndex: range.startFrameIndex,
        endFrameIndex: deletedRange.startFrameIndex - 1
      });
    }
    // Surviving segment after the deleted range
    if (range.endFrameIndex > deletedRange.endFrameIndex) {
      result.push({
        startFrameIndex: deletedRange.endFrameIndex + 1,
        endFrameIndex: range.endFrameIndex
      });
    }
  }
  return result;
};

/**
 * Computes a new relationship store after removing a frame range from the given relationships.
 * Handles: full removal, single-segment trim, and multi-segment split (new IDs).
 */
export function applyFrameRangeDeletion(store: Record<string, OutputRelationshipT>, relationshipIds: string[], frameRange: FrameRangeT) {
  const previousSnapshot: Record<string, OutputRelationshipT> = {};
  const createdIds: string[] = [];
  const updatedStore = {
    ...store
  };
  for (const id of relationshipIds) {
    const rel = store[id];
    if (!rel) continue;
    const remaining = computeRemainingFrameRanges(rel.frameRanges, frameRange);
    if (remaining.length === rel.frameRanges.length) {
      const hasChange = remaining.some((r, i) => r.startFrameIndex !== rel.frameRanges[i]?.startFrameIndex || r.endFrameIndex !== rel.frameRanges[i]?.endFrameIndex);
      if (!hasChange) continue;
    }
    previousSnapshot[id] = rel;
    if (remaining.length === 0) {
      delete updatedStore[id];
    } else if (remaining.length === 1) {
      updatedStore[id] = {
        ...rel,
        frameRanges: remaining
      };
    } else {
      delete updatedStore[id];
      for (const survivingRange of remaining) {
        const newId = uuid();
        createdIds.push(newId);
        updatedStore[newId] = {
          ...rel,
          id: newId,
          frameRanges: [survivingRange]
        };
      }
    }
  }
  return {
    previousSnapshot,
    updatedStore,
    createdIds
  };
}